import { useState } from 'react';

import { ChevronLeft, Info, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { Card } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { getTimeOffStatusText } from '@/modules/common/components/molecules/badge/status-configs/time-off-status-config';
import { FullPageErrorState } from '@/modules/common/components/organisms/full-page-error-state';
import { UnsavedChangesModal } from '@/modules/settings/components/unsaved-changes-modal';
import { formatCompactDateRange } from '@/utils/date-formatters';

import { TimeOffApproveActions } from '../components/details/time-off-approve-actions';
import { TimeOffDayOverlapModal } from '../components/details/time-off-day-overlap-modal';
import { TimeOffMemberInfoCard } from '../components/details/time-off-member-info-card';
import { TimeOffRequestBalanceCard } from '../components/details/time-off-request-balance-card';
import { TimeOffRequestForm } from '../components/details/time-off-request-form';
import {
  TimeOffBalanceCardSkeleton,
  TimeOffRequestSkeleton,
} from '../components/details/time-off-request-skeleton';
import { TimeOffTypesListCard } from '../components/details/time-off-types-list-card';
import { useCancelTimeOff } from '../hooks/use-cancel-time-off';
import { useTimeOffRequestPage } from '../hooks/use-time-off-request-page';
import { TimeOffDetailsMode } from '../types/time-off-details-mode';
import { TimeOffStatus } from '../types/time-off-status';

/**
 * Single request screen for all three modes (read, approve, create), resolved
 * from the route by {@link useTimeOffRequestPage}. The controller owns mode
 * resolution, the form instance, and data loading; this component renders the
 * shared chrome (header + body grid) and switches only the header actions and
 * right column per mode.
 */
export const TimeOffRequestDetailsPage = () => {
  const { t } = useTranslation(['authenticated.timeOff', 'common']);
  const {
    mode,
    form,
    details,
    isLoading,
    isError,
    approveRequest,
    balance,
    isBalanceLoading,
    allBalances,
    requestedDays,
    isCancellable,
    isEditable,
    isMemberView,
    isSubmitting,
    isSubmitDisabled,
    disabledReasons,
    balanceError,
    previewedAvailableAfterBooking,
    dayValidationErrors,
    clearDayValidationErrors,
    startEdit,
    cancelEdit,
    navigateBack,
    submitRequest,
  } = useTimeOffRequestPage();

  const isCreate = mode === TimeOffDetailsMode.Create;
  const isEdit = mode === TimeOffDetailsMode.Edit;
  const isRead = mode === TimeOffDetailsMode.Read;
  const isApprove = mode === TimeOffDetailsMode.Approve;
  const isEditableMode = isCreate || isEdit;

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Create-mode document upload (frontend-only — no upload endpoint yet).
  const [documentFile, setDocumentFile] = useState<File | null>(null);

  // Show the attachment section when the type requires a document. In create
  // the requirement comes from the selected category's balance policy; in read/
  // approve we also show it when the request already carries attachments.
  const requiresDocument =
    (balance?.policy?.requiresDocument ?? false) ||
    (!isCreate && (details?.attachments.length ?? 0) > 0);

  // Only create/edit modes are editable, so only they can have unsaved changes. Read /
  // approve fields are disabled — guarding them would pop the dialog spuriously
  // (the form can still report `isDirty` from hydration/blur side effects).
  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: isEditableMode && form.formState.isDirty,
    onDiscard: () => form.reset(),
  });

  const handleBack = () => {
    if (unsavedChangesGuard.checkUnsavedChanges(navigateBack)) {
      navigateBack();
    }
  };

  const handleCancelEdit = () => {
    if (unsavedChangesGuard.checkUnsavedChanges(cancelEdit)) {
      cancelEdit();
    }
  };

  const cancelMutation = useCancelTimeOff({
    onSuccess: () => {
      setIsCancelModalOpen(false);
      navigateBack();
    },
  });

  if (isError || (!isCreate && !isLoading && !details)) {
    return (
      <FullPageErrorState
        title={t('details.error.title')}
        description={t('details.error.description')}
        actions={
          <Button variant="secondary" onClick={navigateBack}>
            {t('details.error.back')}
          </Button>
        }
      />
    );
  }

  const title = isCreate
    ? t('details.create.title')
    : (details?.type.displayName ?? t('details.create.title'));
  const balanceTitle = isMemberView ? t('details.balance.memberTitle') : undefined;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Header: bordered back button + title (+ status / actions by mode) */}
      <div className="z-[5] shrink-0 border-b border-border bg-surface px-4 py-3 shadow-[0_1px_4px_0_hsl(var(--border))]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 flex-shrink-0 p-0"
              aria-label={t('common:back')}
              onClick={handleBack}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate text-xl font-semibold leading-none text-foreground sm:text-2xl">
                {title}
              </h1>
              {!isCreate && details && (
                <StatusBadge
                  type={StatusBadgeType.TimeOff}
                  status={details.status}
                  label={getTimeOffStatusText(details.status)}
                />
              )}
            </div>
          </div>

          {!isLoading && (
            <div className="flex shrink-0 items-center gap-2">
              {isApprove && approveRequest && (
                <TimeOffApproveActions request={approveRequest} onReviewed={navigateBack} />
              )}

              {isRead && isEditable && (
                <Button
                  variant="outline"
                  className="shrink-0"
                  onClick={() => setIsEditModalOpen(true)}
                >
                  <Pencil className="h-4 w-4" />
                  {t('details.editRequest')}
                </Button>
              )}

              {isRead && isCancellable && (
                <Button
                  variant="outlineDestructive"
                  className="shrink-0"
                  onClick={() => setIsCancelModalOpen(true)}
                >
                  {t('details.cancelRequest')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submitRequest)}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          {/* Body: form (left) + right column. Widened to fill laptop screens
              (`max-w-7xl`) while staying readable on very large ones. The right
              column comes first in the DOM with `lg:order-last` so it sits ABOVE
              the form on small screens and to the RIGHT on large ones, where it
              also sticks while the left form scrolls past it. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-6">
            {isLoading ? (
              <TimeOffRequestSkeleton />
            ) : (
              <div className="grid w-full max-w-7xl grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
                <div className="flex flex-col gap-4 lg:sticky lg:top-0 lg:order-last">
                  {isApprove && details ? (
                    <TimeOffMemberInfoCard
                      requestedBy={details.requestedBy}
                      requestedDays={requestedDays}
                    />
                  ) : isBalanceLoading ? (
                    // Category just changed → show a skeleton, not the all-types card.
                    <TimeOffBalanceCardSkeleton />
                  ) : balance ? (
                    <TimeOffRequestBalanceCard
                      balance={balance}
                      title={balanceTitle}
                      requestedDays={requestedDays}
                      isPending={isEditableMode || details?.status === TimeOffStatus.Pending}
                      error={balanceError}
                      previewedAvailableAfterBooking={previewedAvailableAfterBooking}
                    />
                  ) : (
                    // Create mode, no category picked yet → show the type list so the
                    // user can see their balances before choosing.
                    isCreate &&
                    allBalances.length > 0 && <TimeOffTypesListCard balances={allBalances} />
                  )}
                </div>

                <Card className="p-6">
                  <TimeOffRequestForm
                    mode={mode}
                    control={form.control}
                    setValue={form.setValue}
                    details={details}
                    requiresDocument={requiresDocument}
                    documentFile={documentFile}
                    onDocumentFileChange={setDocumentFile}
                    policy={balance?.policy}
                  />
                </Card>
              </div>
            )}
            <div className="h-6 shrink-0" />
          </div>

          {/* Footer submit — create mode only. Disabled while the form is invalid
              or the request exceeds the balance; the why is a bulleted tooltip. */}
          {!isLoading && isEditableMode && (
            <div className="flex shrink-0 justify-end gap-2 border-t border-border bg-surface px-4 py-3">
              {isEdit && (
                <Button type="button" variant="outline" onClick={handleCancelEdit}>
                  {t('common:cancel')}
                </Button>
              )}
              <SimpleTooltip
                content={
                  isSubmitDisabled && disabledReasons.length > 0 ? (
                    <ul className="m-0 list-disc space-y-1 pl-4 text-xs">
                      {disabledReasons.map(reason => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  ) : undefined
                }
              >
                {/* A disabled button doesn't fire hover events, so the span captures
                    the tooltip trigger (mirrors the generate-entity footer). */}
                <span className="inline-flex">
                  <ButtonLoadable
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitDisabled}
                  >
                    {isSubmitDisabled && <Info className="h-4 w-4" />}
                    {isEdit ? t('details.edit.submit') : t('details.create.submit')}
                  </ButtonLoadable>
                </span>
              </SimpleTooltip>
            </div>
          )}
        </form>
      </Form>

      {/* Cancel request confirmation (read mode) */}
      {details && (
        <ConfirmationModal
          open={isCancelModalOpen}
          onOpen={() => setIsCancelModalOpen(true)}
          onClose={() => setIsCancelModalOpen(false)}
          title={t('cancelModal.title')}
          message={t('cancelModal.message', {
            type: details.type.displayName,
            dateRange: formatCompactDateRange(details.startDate, details.endDate),
          })}
          confirmText={t('cancelModal.confirm')}
          cancelText={t('cancelModal.cancel')}
          confirmVariant="destructive"
          isLoading={cancelMutation.isPending}
          onConfirm={async () => {
            await cancelMutation.mutateAsync(details.id);
          }}
        />
      )}

      <ConfirmationModal
        open={isEditModalOpen}
        onOpen={() => setIsEditModalOpen(true)}
        onClose={() => setIsEditModalOpen(false)}
        title={t('editModal.title')}
        message={t('editModal.message')}
        confirmText={t('editModal.confirm')}
        cancelText={t('editModal.cancel')}
        confirmVariant="default"
        onConfirm={closeModal => {
          closeModal();
          startEdit();
        }}
      />

      <UnsavedChangesModal
        open={unsavedChangesGuard.isModalOpen}
        onClose={unsavedChangesGuard.handleModalCancel}
        onDiscard={unsavedChangesGuard.handleModalDiscard}
      />

      {/* Day-level overlap errors (backend code 2808) from a rejected create. */}
      <TimeOffDayOverlapModal
        open={dayValidationErrors.length > 0}
        onClose={clearDayValidationErrors}
        errors={dayValidationErrors}
      />
    </div>
  );
};
